import { abbreviate } from '@chainflip/utils/string';
import Button from '@/shared/components/flip-ui-kit/Button';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import {
  type FundAccountModalProps,
  FundFlipModal,
} from '@/shared/components/PolkadotConnectionButton/FundFlipModal';
import NavbarDropdown, {
  type NavbarDropdownItem,
} from '@/shared/components/PolkadotConnectionButton/NavbarDropdown';
import RedeemFlipModal from '@/shared/components/PolkadotConnectionButton/RedeemFlipModal';
import { ConnectAvatar } from '@/shared/components/RainbowKit';
import useBoolean from '@/shared/hooks/useBoolean';
import {
  type InjectedAccountWithMeta,
  type ReadonlyAccount,
  usePolkadot,
} from '@/shared/hooks/usePolkadot';
import PowerIcon from '@/shared/icons/flip-ui-kit/large/Power';
import { chainflipAssetMap } from '@/shared/utils/env';
import type ExtrinsicSDK from '@/shared/utils/ExtrinsicSDK';
import TokenAmount from '@/shared/utils/TokenAmount';

export const PolkaConnectInfo = ({
  flipBalance,
  account,
  redeemFlip,
  connectText,
  openAccountModal,
  additionalActions = [],
  type,
  boundRedeemAddress,
  hasOnboarded,
  onDisconnect,
}: {
  flipBalance: TokenAmount | null;
  account: InjectedAccountWithMeta | ReadonlyAccount | null;
  redeemFlip: typeof ExtrinsicSDK.prototype.redeemFlip;
  connectText?: string;
  openAccountModal?: () => void;
  additionalActions?: {
    content: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    separator?: React.ReactNode;
  }[];
  type: FundAccountModalProps['type'];
  boundRedeemAddress?: `0x${string}`;
  hasOnboarded?: boolean;
  onDisconnect?: () => void;
}) => {
  const { disconnect, selectedAccount: polkadotAccount } = usePolkadot();

  const {
    value: isActive,
    setTrue: activateDropdown,
    setFalse: deactivateDropdown,
  } = useBoolean(false);

  const {
    value: fundAccountOpened,
    setTrue: openFundAccountModal,
    setFalse: closeFundAccountModal,
  } = useBoolean(false);

  const {
    value: redeemModalOpen,
    setTrue: openRedeemModal,
    setFalse: closeRedeemModal,
  } = useBoolean(false);

  const token = chainflipAssetMap.Flip;

  if (polkadotAccount === null) {
    if (!connectText || !openAccountModal) return null;

    return (
      <Button
        type="secondary-standard"
        className="flex items-center gap-x-2 whitespace-nowrap bg-transparent"
        onClick={openAccountModal}
      >
        {connectText}
      </Button>
    );
  }

  const address =
    polkadotAccount.type === 'evm' ? polkadotAccount.evmAddress : polkadotAccount.idSs58;

  return (
    <>
      <NavbarDropdown
        title={
          <Button
            type="secondary-standard"
            className="flex items-center gap-x-2 whitespace-nowrap bg-transparent"
          >
            {abbreviate(address)}
            <ConnectAvatar address={address} size={16} />
          </Button>
        }
        items={
          [
            polkadotAccount.type === 'evm' && {
              content: (
                <div className="flex flex-col">
                  <span className="text-cf-light-2">EVM Account</span>
                  <div className="flex flex-row items-center justify-start gap-x-1">
                    {abbreviate(polkadotAccount.evmAddress)}
                    <CopyButton textToCopy={polkadotAccount.evmAddress} />
                  </div>
                </div>
              ),
            },
            {
              content: (
                <div className="flex flex-col">
                  <div className="text-cf-light-2">
                    {polkadotAccount.type === 'evm' ? (
                      <Tooltip
                        content={`Your Chainflip account address was created from the signature of the Ethereum address ${abbreviate(polkadotAccount.evmAddress)}`}
                        childrenClassName="underline decoration-dotted underline-offset-2"
                        placement="left"
                        tooltipClassName="w-[225px]"
                      >
                        Chainflip Account
                      </Tooltip>
                    ) : (
                      'Chainflip Account'
                    )}
                  </div>
                  <div className="flex flex-row items-center justify-start gap-x-1">
                    {abbreviate(polkadotAccount.idSs58)}
                    <CopyButton textToCopy={polkadotAccount.idSs58} />
                  </div>
                </div>
              ),
            },
            {
              content: (
                <div className="flex flex-col">
                  <span className="text-cf-light-2">{token.symbol} Balance</span>
                  <FlipLabel large amount={flipBalance ?? TokenAmount.ZERO} hideSymbol />
                </div>
              ),
            },
            ...(polkadotAccount.type !== 'evm' || hasOnboarded
              ? [
                  {
                    content: <span className="font-aeonikMedium">Fund account</span>,
                    onClick: openFundAccountModal,
                    disabled: polkadotAccount.readonly,
                  },
                  {
                    content: <span className="font-aeonikMedium">Redeem funds</span>,
                    onClick: openRedeemModal,
                    disabled: polkadotAccount.readonly,
                  },
                  ...additionalActions,
                ]
              : []),
            {
              content: (
                <div className="flex items-center gap-x-1 font-aeonikMedium">
                  <PowerIcon className="text-cf-red-1" />
                  Disconnect
                </div>
              ),
              separator: <hr className="border-cf-gray-4" />,
              onClick: onDisconnect ?? disconnect,
            },
          ].filter(Boolean) as NavbarDropdownItem[]
        }
        isActive={isActive}
        onMouseEnter={activateDropdown}
        onMouseLeave={deactivateDropdown}
      />

      <FundFlipModal onClose={closeFundAccountModal} open={fundAccountOpened} type={type} />
      <RedeemFlipModal
        onClose={closeRedeemModal}
        open={redeemModalOpen}
        account={account}
        flipBalance={flipBalance}
        redeemFlip={redeemFlip}
        boundRedeemAddress={boundRedeemAddress}
      />
    </>
  );
};
